import Input from "components/input";
import InputNumber from "components/input-number";
import Radio from 'components/radio';
import RadioGroup from 'components/radio-group';
import PropTypes from "prop-types";
import { Col, Row } from "react-flexbox-grid";
import intl from "react-intl-universal";
import { formatNumber } from "utils/format";
import { isCurrentRefuelingPointPercentDisabled } from '../../validations';

const TIPO_ALGORITMO_POLITICA_SILOS = 3;
const INTL_GERAL = 'master-data.general-configuration';

export const STOCK_POLICY = {
  OBJECTIVE: 'objectivePolicy',
  DAILY_DOI: 'dailyDynamicIP',
  WEEKLY_DOI: 'weeklyDynamicPolice',
};

export const INVENTORY_POLICY_MODEL = {
  [STOCK_POLICY.OBJECTIVE]: 0,
  [STOCK_POLICY.WEEKLY_DOI]: 1,
  [STOCK_POLICY.DAILY_DOI]: 2,
};

export const mapInventoryPolicyModelToStockPolicy = (value) => {
  const map = {
    0: STOCK_POLICY.OBJECTIVE,
    1: STOCK_POLICY.WEEKLY_DOI,
    2: STOCK_POLICY.DAILY_DOI,
  };
  return map[value] ?? STOCK_POLICY.OBJECTIVE;
};

const InventoryConfiguration = ({
  labeledFamily,
  userCanEditStockPolicies,
  selectedTipoAlgoritmo,
  campoPercentual,
  changeValueDecimal,
  changeDecimalValue,
  selectedCurrentStockPolicy,
  onStockPolicyChange,
  featureFlagDynamicInventoryPolicies,
  featureFlagDoiDynamicInventoryPolicy,
  doiMinimum,
  doiTarget,
  doiMaximum,
  doiFloor,
  onChangeDoiField,
  selectedUseStorageLocations,
}) => {
  const isDailyDoi = selectedCurrentStockPolicy === STOCK_POLICY.DAILY_DOI;
  const isWeeklyDoi = selectedCurrentStockPolicy === STOCK_POLICY.WEEKLY_DOI;
  const canEdit = userCanEditStockPolicies();

  const handlePolicyChange = (event) => {
    onStockPolicyChange(event.target.value);
  };

  return (
    <>
      <label className="label-style">{intl.get(`${INTL_GERAL}.currentInventory`)}</label>
      <div className="container__general-form">
        <Row>
          <Col xs={12}>
            <RadioGroup directionItems="row" name="currentStockPolicy">
              <Radio
                checked={selectedCurrentStockPolicy === STOCK_POLICY.OBJECTIVE}
                text={intl.get(`master-data.general.actions.staticPolicy`)}
                value={STOCK_POLICY.OBJECTIVE}
                onChange={handlePolicyChange}
                data-testid="radio-objective-policy"
              />
              {featureFlagDoiDynamicInventoryPolicy && (
                <Radio
                  checked={selectedCurrentStockPolicy === STOCK_POLICY.DAILY_DOI}
                  text={intl.get(`master-data.general.actions.doiPolicy`)}
                  value={STOCK_POLICY.DAILY_DOI}
                  onChange={handlePolicyChange}
                  disabled={selectedUseStorageLocations}
                  data-testid="radio-daily-dynamic-ip"
                />
              )}
              {featureFlagDynamicInventoryPolicies && (
                <Radio
                  checked={selectedCurrentStockPolicy === STOCK_POLICY.WEEKLY_DOI}
                  text={intl.get(`master-data.general.actions.weeklyPolicy`)}
                  value={STOCK_POLICY.WEEKLY_DOI}
                  onChange={handlePolicyChange}
                  disabled={selectedUseStorageLocations}
                  data-testid="radio-weekly-dynamic-police"
                />
              )}
            </RadioGroup>
          </Col>
        </Row>

        {!isDailyDoi && (
          <Row>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.minStock`)}
                name="EstoqueMinimoAtual"
                value={labeledFamily.EstoqueMinimoAtual}
                onChange={(_value) => changeValueDecimal("EstoqueMinimoAtual", _value)}
                disabled={isWeeklyDoi || !canEdit}
                data-testid="input-estoque-minimo-atual"
              />
            </Col>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.maxStock`)}
                name="EstoqueMaximoAtual"
                value={labeledFamily.EstoqueMaximoAtual}
                onChange={(_value) => changeValueDecimal("EstoqueMaximoAtual", _value)}
                disabled={
                  isWeeklyDoi || selectedTipoAlgoritmo == TIPO_ALGORITMO_POLITICA_SILOS || !canEdit
                }
                data-testid="input-estoque-maximo-atual"
              />
            </Col>
            <Col xs={3} lg={3} md={3}>
              <InputNumber
                disabled={
                  isWeeklyDoi || isCurrentRefuelingPointPercentDisabled(selectedTipoAlgoritmo)
                }
                required
                label={intl.get(`${INTL_GERAL}.refuelingPoint`)}
                type="default"
                maxLength="3"
                value={campoPercentual}
                name="PontoReabastecimentoAtualPerc"
                onChange={(_value) => changeDecimalValue("PontoReabastecimentoAtualPerc", _value)}
                data-testid="input-ponto-reabastecimento-atual-perc"
              />
            </Col>
            <Col xs={2} lg={2} md={2}>
              <Input
                disabled
                label={intl.get(`${INTL_GERAL}.inQuantity`)}
                name="PontoReabastecimentoAtual"
                value={formatNumber(labeledFamily.PontoReabastecimentoAtual)}
              />
            </Col>
          </Row>
        )}

        {isDailyDoi && (
          <Row>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.doiMinimum`)}
                name="DoiMinimum"
                value={doiMinimum}
                onChange={(_value) => onChangeDoiField("DoiMinimum", _value)}
                disabled={!canEdit}
                helperText={intl.get(`${INTL_GERAL}.doiWholeDaysHelperText`)}
                data-testid="input-doi-minimum"
              />
            </Col>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.doiTarget`)}
                name="DoiTarget"
                value={doiTarget}
                onChange={(_value) => onChangeDoiField("DoiTarget", _value)}
                disabled={!canEdit}
                helperText={intl.get(`${INTL_GERAL}.doiWholeDaysHelperText`)}
                data-testid="input-doi-target"
              />
            </Col>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.doiMaximum`)}
                name="DoiMaximum"
                value={doiMaximum}
                onChange={(_value) => onChangeDoiField("DoiMaximum", _value)}
                disabled={!canEdit}
                helperText={intl.get(`${INTL_GERAL}.doiWholeDaysHelperText`)}
                data-testid="input-doi-maximum"
              />
            </Col>
            <Col xs={2} lg={2} md={2}>
              <InputNumber
                type="default"
                required
                label={intl.get(`${INTL_GERAL}.doiFloor`)}
                name="DoiFloor"
                value={doiFloor}
                onChange={(_value) => onChangeDoiField("DoiFloor", _value)}
                disabled={!canEdit}
                helperText={intl.get(`${INTL_GERAL}.doiFloorHelperText`)}
                data-testid="input-doi-floor"
                allowDecimals={true}
                decimalsLimit={2}
              />
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

InventoryConfiguration.propTypes = {
  labeledFamily: PropTypes.shape({
    EstoqueMinimoAtual: PropTypes.number,
    EstoqueMaximoAtual: PropTypes.number,
    PontoReabastecimentoAtual: PropTypes.number,
  }).isRequired,
  userCanEditStockPolicies: PropTypes.func.isRequired,
  selectedTipoAlgoritmo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  campoPercentual: PropTypes.number,
  changeValueDecimal: PropTypes.func.isRequired,
  changeDecimalValue: PropTypes.func.isRequired,
  selectedCurrentStockPolicy: PropTypes.string.isRequired,
  onStockPolicyChange: PropTypes.func.isRequired,
  featureFlagDynamicInventoryPolicies: PropTypes.bool.isRequired,
  featureFlagDoiDynamicInventoryPolicy: PropTypes.bool.isRequired,
  doiMinimum: PropTypes.number,
  doiTarget: PropTypes.number,
  doiMaximum: PropTypes.number,
  doiFloor: PropTypes.number,
  onChangeDoiField: PropTypes.func.isRequired,
  selectedUseStorageLocations: PropTypes.bool,
};

export default InventoryConfiguration;
